import { type FormikErrors } from "formik";
import {
  type CriterionBoundary,
  type EligibilityConditions,
  type EligibilityDataPoint,
} from "src/generated/hooks/form-service/requests";
import { validateWithZod } from "src/services/formValidations";
import { z } from "zod";
import {
  addressConditionSchema,
  addressConditionToComparison,
  comparisonToAddressCondition,
  describeAddressCondition,
  type AddressCondition,
} from "./addressCondition";
import {
  answerSelectionSchema,
  answerSelectionToComparison,
  comparisonToAnswerSelection,
  describeAnswerSelection,
  type AnswerSelectionCondition,
} from "./answerSelection";
import {
  capacityConditionSchema,
  capacityConditionToComparison,
  comparisonToCapacityCondition,
  describeCapacityCondition,
  type CapacityCondition,
} from "./capacityCondition";
import {
  comparisonToDateCondition,
  dateConditionSchema,
  dateConditionToComparison,
  describeDateCondition,
  type DateCondition,
} from "./dateCondition";

export type MatchMode = "all" | "any";

export type Condition =
  | DateCondition
  | AddressCondition
  | CapacityCondition
  | AnswerSelectionCondition;

export type ConditionsFormValues = {
  matchMode: MatchMode;
  conditions: Condition[];
  inheritConditions?: boolean;
};

export const emptyConditionsFormValues: ConditionsFormValues = {
  matchMode: "all",
  conditions: [],
};

const conditionSchemas = {
  date: dateConditionSchema,
  address: addressConditionSchema,
  capacity: capacityConditionSchema,
  answerSelection: answerSelectionSchema,
};

function isConditionKind(kind: unknown): kind is keyof typeof conditionSchemas {
  return (
    typeof kind === "string" &&
    Object.prototype.hasOwnProperty.call(conditionSchemas, kind)
  );
}

const formConditionSchema = z.any().superRefine((condition, ctx) => {
  const kind =
    typeof condition === "object" && condition !== null && "kind" in condition
      ? condition.kind
      : undefined;
  if (!isConditionKind(kind)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Unsupported condition",
    });
    return;
  }
  const parsed = conditionSchemas[kind].safeParse(condition);
  if (parsed.success) return;
  for (const issue of parsed.error.issues) {
    ctx.addIssue(issue);
  }
});

export const conditionsFormSchema = z
  .object({
    matchMode: z.enum(["all", "any"]),
    conditions: z.array(formConditionSchema),
    inheritConditions: z.boolean().optional(),
  })
  .superRefine((values, ctx) => {
    if (
      values.conditions.length === 0 &&
      values.inheritConditions === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["conditions"],
        message: "At least one condition is required",
      });
    }
  });

export function validateConditionsForm(
  values: ConditionsFormValues
): Promise<FormikErrors<ConditionsFormValues> | undefined> {
  return validateWithZod(
    conditionsFormSchema as z.ZodType<ConditionsFormValues>
  )(values);
}

type ConditionsFormValuesResult =
  | { status: "empty" }
  | { status: "editable"; values: ConditionsFormValues }
  | { status: "unsupported" };

export type ConditionsDescription =
  | { type: "none" }
  | { type: "list"; matchMode: MatchMode; lines: string[] };

export const unsupportedConditionsError = new Error(
  "These conditions aren't supported."
);

export function parseCriterion(
  criterion: {
    conditions?: EligibilityConditions | null;
    dataPoints?: EligibilityDataPoint[];
    boundaries?: CriterionBoundary[];
  } | null
): { description: ConditionsDescription; formValues: ConditionsFormValues } {
  if (criterion?.conditions == null) {
    return {
      description: { type: "none" },
      formValues: emptyConditionsFormValues,
    };
  }

  const parsed = conditionsObjectToFormValues(criterion.conditions);
  if (parsed.status === "unsupported") throw unsupportedConditionsError;
  if (parsed.status === "empty" || parsed.values.conditions.length === 0) {
    return {
      description: { type: "none" },
      formValues: emptyConditionsFormValues,
    };
  }

  const dataPointIdNameMapping = new Map(
    (criterion.dataPoints ?? []).map((point) => [point.id, point.name])
  );
  const boundaryIdNameMapping = new Map(
    (criterion.boundaries ?? []).map((boundary) => [
      boundary.id,
      boundary.name || boundary.key || "Unnamed boundary",
    ])
  );

  return {
    description: {
      type: "list",
      matchMode: parsed.values.matchMode,
      lines: parsed.values.conditions.map((condition) =>
        describeCondition(
          condition,
          dataPointIdNameMapping,
          boundaryIdNameMapping
        )
      ),
    },
    formValues: parsed.values,
  };
}

export function formValuesToConditionsObject(
  values: ConditionsFormValues
): EligibilityConditions {
  const comparisons = values.conditions.map(conditionToComparison);
  if (comparisons.length === 0) return { expression: null };
  if (values.matchMode === "any") {
    return {
      expression: {
        anyOf: comparisons.map((comparison) => ({ allOf: [comparison] })),
      },
    };
  }
  return { expression: { anyOf: [{ allOf: comparisons }] } };
}

function comparisonToCondition(
  comparison: Parameters<typeof comparisonToDateCondition>[0]
): Condition | null {
  switch (comparison.kind) {
    case "Date":
      return comparisonToDateCondition(comparison);
    case "Address":
      return comparisonToAddressCondition(comparison);
    case "Numeric":
      return comparisonToCapacityCondition(comparison);
    case "Membership":
      return comparisonToAnswerSelection(comparison);
  }
}

function conditionToComparison(condition: Condition) {
  switch (condition.kind) {
    case "date":
      return dateConditionToComparison(condition);
    case "address":
      return addressConditionToComparison(condition);
    case "capacity":
      return capacityConditionToComparison(condition);
    case "answerSelection":
      return answerSelectionToComparison(condition);
    default:
      throw new Error("Unsupported condition");
  }
}

function describeCondition(
  condition: Condition,
  dataPoints: Map<string, string>,
  boundaries: Map<string, string>
): string {
  switch (condition.kind) {
    case "capacity":
      return describeCapacityCondition(condition);
    case "answerSelection":
      return describeAnswerSelection(condition, dataPoints);
    case "date":
      return describeDateCondition(condition, dataPoints);
    case "address":
      return describeAddressCondition(condition, dataPoints, boundaries);
    default:
      throw new Error("Unsupported condition");
  }
}

function conditionsObjectToFormValues(
  conditions: EligibilityConditions
): ConditionsFormValuesResult {
  if (conditions.expression == null) return { status: "empty" };

  const groups = conditions.expression.anyOf;
  if (groups.length === 0 || groups.some((group) => group.allOf.length === 0)) {
    return { status: "unsupported" };
  }

  const isAll = groups.length === 1;
  const isAny = groups.every((group) => group.allOf.length === 1);
  if (!isAll && !isAny) return { status: "unsupported" };

  const formConditions: Condition[] = [];
  for (const comparison of groups.flatMap((group) => group.allOf)) {
    const condition = comparisonToCondition(comparison);
    if (!condition) return { status: "unsupported" };
    formConditions.push(condition);
  }

  return {
    status: "editable",
    values: {
      matchMode: isAll ? "all" : "any",
      conditions: formConditions,
    },
  };
}
